import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import TabBar from '../dashboard/TabBar';
import CoralHeader from '../dashboard/CoralHeader';
import type { TabId } from '../dashboard/useTabParam';

export type OwnerTabId = 'counter' | 'stats' | 'setup' | 'more';

// Owner mode is a MODE SWAP of the customer TabBar, not a nested nav: the
// same four physical slots (home/pass/loot/more) are relabelled COUNTER
// / STATS / SETUP / MORE and routed to existing owner pages instead of
// switching in-place ?tab= content. Each slot is a real route, so "active"
// here just tells TabBar which button to highlight — the actual page
// (ShopOwnerDashboard, ShopAnalyticsDashboard, SettingsHub family,
// OwnerMorePanel) is whichever route rendered this OwnerShell.
const OWNER_LABELS: Record<TabId, string> = {
  home: 'COUNTER', pass: 'STATS', loot: 'SETUP', more: 'MORE',
};

const ACTIVE_TO_TAB_ID: Record<OwnerTabId, TabId> = {
  counter: 'home', stats: 'pass', setup: 'loot', more: 'more',
};

const TAB_ID_TO_ROUTE: Record<TabId, string> = {
  home: '/shops/owner/dashboard',
  pass: '/shop/analytics',
  loot: '/shops/settings',
  more: '/shops/owner/more',
};

interface OwnerShellProps {
  active: OwnerTabId;
  header: ReactNode;
  children: ReactNode;
}

// Chrome for the four owner-mode routes. Not wrapped by MainLayout or
// GameLayout — this IS the page-level layout for owner routes (hence
// `safe-shell` here rather than relying on an outer shell to supply it).
// Each owner page builds its own CoralHeader (page-specific eyebrow/title/
// chip come from that page's own state) and passes it in as `header`;
// OwnerShell itself stays state-free besides routing the tab clicks.
export default function OwnerShell({ active, header, children }: OwnerShellProps) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col h-dvh bg-background">
      {header}
      {/* App frame: header + tab bar pinned, the page content scrolls between them */}
      <main className="flex-1 min-h-0 px-[22px] pt-6 pb-3 overflow-y-auto overscroll-contain">{children}</main>
      <TabBar
        active={ACTIVE_TO_TAB_ID[active]}
        labels={OWNER_LABELS}
        onSelect={(tabId) => navigate(TAB_ID_TO_ROUTE[tabId])}
      />
    </div>
  );
}

interface OwnerLoadingShellProps {
  active: OwnerTabId;
  statusLabel: string;
  title: string;
  children: ReactNode;
}

// Fix round 1 (review): the 5 settings-family pages have early-return
// loading/"no shop" branches that render BEFORE the page builds its real
// CoralHeader (which needs loaded data — shop name, save handlers, etc).
// Before Task 12, MainLayout gave those branches a Header (logo home-link,
// logout) for free; dropping MainLayout in favour of OwnerShell left them
// with zero navigation — no tab bar, no way to reach MORE → log out, just
// browser-back. This is the minimal substitute: same OwnerShell, a bare
// CoralHeader with no chip (there's nothing loaded yet to act on), so the
// tab bar (and therefore log out) stays reachable on every branch, not just
// the loaded one. Lives here rather than a new file — it's a 3-line
// wrapper, not a component with its own concerns.
export function OwnerLoadingShell({ active, statusLabel, title, children }: OwnerLoadingShellProps) {
  return (
    <OwnerShell
      active={active}
      header={<CoralHeader statusLabel={statusLabel} eyebrow="OWNER" title={title} />}
    >
      {children}
    </OwnerShell>
  );
}
